import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CubeIcon,
  RectangleStackIcon,
  ShoppingBagIcon,
  PlusIcon,
  EyeIcon,
  MegaphoneIcon,
  CurrencyDollarIcon,
  CursorArrowRaysIcon,
  CheckBadgeIcon,
  XCircleIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import AdminLayout from '../../components/AdminLayout';

interface DashboardStats {
  totalProducts: number;
  totalCollections: number;
  totalOrders: number;
  adsOrders: number;
  adsRevenue: number;
  adViews: number;
  adClicks: number;
  adSuccesses: number;
  adDropOffs: number;
}

const AdminDashboard = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalProducts: 0,
    totalCollections: 0,
    totalOrders: 0,
    adsOrders: 0,
    adsRevenue: 0,
    adViews: 0,
    adClicks: 0,
    adSuccesses: 0,
    adDropOffs: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        const headers = { Authorization: `Bearer ${token}` };

        const [colRes, prodRes, orderRes, trackingRes] = await Promise.all([
          fetch('/api/collections/admin/all', { headers }),
          fetch('/api/products/admin/all', { headers }),
          fetch('/api/orders/stats/dashboard', { headers }),
          fetch('/api/tracking/stats', { headers }),
        ]);

        const [colData, prodData, orderData, trackingData] = await Promise.all([
          colRes.json(),
          prodRes.json(),
          orderRes.json(),
          trackingRes.json(),
        ]);

        setStats({
          totalProducts: prodData.success ? prodData.data.products.length : 0,
          totalCollections: colData.success ? colData.data.length : 0,
          totalOrders: orderData.success ? orderData.data.totalOrders : 0,
          adsOrders: orderData.success ? orderData.data.adsOrders : 0,
          adsRevenue: orderData.success ? orderData.data.adsRevenue : 0,
          adViews: trackingData.success ? trackingData.data.views : 0,
          adClicks: trackingData.success ? trackingData.data.clicks : 0,
          adSuccesses: trackingData.success ? trackingData.data.successes : 0,
          adDropOffs: trackingData.success ? trackingData.data.dropOffs : 0,
        });
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="flex items-center justify-center py-32">
          <div className="spinner-lg" />
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    { label: 'Products', value: stats.totalProducts, icon: CubeIcon, color: 'bg-blue-50 text-blue-600' },
    { label: 'Collections', value: stats.totalCollections, icon: RectangleStackIcon, color: 'bg-green-50 text-green-600' },
    { label: 'Orders', value: stats.totalOrders, icon: ShoppingBagIcon, color: 'bg-amber-50 text-amber-600' },
  ];

  const adsConversionRate = stats.totalOrders > 0
    ? Math.round((stats.adsOrders / stats.totalOrders) * 100)
    : 0;

  const clickToOrderRate = stats.adClicks > 0
    ? Math.round((stats.adSuccesses / stats.adClicks) * 100)
    : 0;

  const quickActions = [
    { name: 'Add Product', href: '/admin/products', icon: PlusIcon, desc: 'Create a new product listing' },
    { name: 'View Orders', href: '/admin/orders', icon: ShoppingBagIcon, desc: 'Manage customer orders' },
    { name: 'Preview Site', href: '/', icon: EyeIcon, desc: 'View the live store' },
  ];

  return (
    <AdminLayout title="Dashboard" subtitle="Overview of your store">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {statCards.map((card) => (
          <div key={card.label} className="bg-white border border-warmgray-200 p-6">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 flex items-center justify-center ${card.color}`}>
                <card.icon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs tracking-wider uppercase text-warmgray-500">{card.label}</p>
                <p className="font-display text-2xl text-offblack">{card.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Ads Performance */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <MegaphoneIcon className="w-5 h-5 text-blue-500" />
          <h2 className="font-display text-lg text-offblack">Facebook Ads Performance</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Ads Orders */}
          <div className="bg-white border border-blue-100 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-50 text-blue-600">
                <MegaphoneIcon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs tracking-wider uppercase text-warmgray-500">Ads Orders</p>
                <p className="font-display text-2xl text-offblack">{stats.adsOrders}</p>
              </div>
            </div>
          </div>

          {/* Ads Revenue */}
          <div className="bg-white border border-blue-100 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-purple-50 text-purple-600">
                <CurrencyDollarIcon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs tracking-wider uppercase text-warmgray-500">Ads Revenue</p>
                <p className="font-display text-2xl text-offblack">{stats.adsRevenue.toLocaleString()} <span className="text-sm text-warmgray-400">DA</span></p>
              </div>
            </div>
          </div>

          {/* Conversion share */}
          <div className="bg-white border border-blue-100 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-green-50 text-green-600 text-lg font-bold font-display">
                %
              </div>
              <div>
                <p className="text-xs tracking-wider uppercase text-warmgray-500">% from Ads</p>
                <p className="font-display text-2xl text-offblack">{adsConversionRate}%</p>
              </div>
            </div>
            <div className="mt-4 h-1.5 bg-warmgray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-700"
                style={{ width: `${adsConversionRate}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Ads Funnel */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <CursorArrowRaysIcon className="w-5 h-5 text-purple-500" />
          <h2 className="font-display text-lg text-offblack">Ads Funnel Tracking</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
          <div className="bg-white border border-purple-100 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-indigo-50 text-indigo-600">
                <UsersIcon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs tracking-wider uppercase text-warmgray-500">Page Views</p>
                <p className="font-display text-2xl text-offblack">{stats.adViews}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-purple-100 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-purple-50 text-purple-600">
                <CursorArrowRaysIcon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs tracking-wider uppercase text-warmgray-500">Order Clicks</p>
                <p className="font-display text-2xl text-offblack">{stats.adClicks}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-purple-100 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-green-50 text-green-600">
                <CheckBadgeIcon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs tracking-wider uppercase text-warmgray-500">Completed</p>
                <p className="font-display text-2xl text-offblack">{stats.adSuccesses}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-purple-100 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-50 text-red-500">
                <XCircleIcon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs tracking-wider uppercase text-warmgray-500">Drop-offs</p>
                <p className="font-display text-2xl text-offblack">{stats.adDropOffs}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-purple-100 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-50 text-amber-600 text-lg font-bold font-display">
                %
              </div>
              <div>
                <p className="text-xs tracking-wider uppercase text-warmgray-500">Conversion</p>
                <p className="font-display text-2xl text-offblack">{clickToOrderRate}%</p>
              </div>
            </div>
            <div className="mt-4 h-1.5 bg-warmgray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-purple-500 rounded-full transition-all duration-700"
                style={{ width: `${clickToOrderRate}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="font-display text-lg text-offblack mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {quickActions.map((action) => (
            <Link
              key={action.name}
              to={action.href}
              className="bg-white border border-warmgray-200 p-5 hover:border-offblack transition-colors group"
            >
              <action.icon className="w-5 h-5 text-warmgray-400 group-hover:text-offblack transition-colors mb-3" />
              <h3 className="text-sm font-medium text-offblack">{action.name}</h3>
              <p className="text-xs text-warmgray-500 mt-1">{action.desc}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Activity — placeholder */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-warmgray-200 p-6">
          <h2 className="font-display text-lg text-offblack mb-4">Recent Products</h2>
          <div className="text-center py-8">
            <CubeIcon className="w-10 h-10 mx-auto text-warmgray-300 mb-2" />
            <p className="text-sm text-warmgray-500">Products will appear here</p>
            <Link to="/admin/products" className="text-xs text-offblack underline mt-2 inline-block">
              Manage Products
            </Link>
          </div>
        </div>
        <div className="bg-white border border-warmgray-200 p-6">
          <h2 className="font-display text-lg text-offblack mb-4">Recent Orders</h2>
          <div className="text-center py-8">
            <ShoppingBagIcon className="w-10 h-10 mx-auto text-warmgray-300 mb-2" />
            <p className="text-sm text-warmgray-500">Orders will appear here</p>
            <Link to="/admin/orders" className="text-xs text-offblack underline mt-2 inline-block">
              View All Orders
            </Link>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
